import React, { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import VaultPickerModal from "./ChatterlyVaultPicker";
import {
  MessageCircle,
  Send,
  RefreshCw,
  X,
  Check,
  ArrowLeft,
  DollarSign,
  Search,
  Bell,
  BellOff,
  Volume2,
  VolumeX,
  Paperclip,
  Lock,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_MS = 25000;

/**
 * Mensajería global de Chatterly.
 * Muestra el ÚLTIMO mensaje de cada conversación de TODAS las creators, ordenado por timestamp desc.
 * Refresca cada 25s. Click en un mensaje → abre historial + textarea para intervenir.
 */
export default function ChatterlyLivePanel() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [polling, setPolling] = useState(false);
  const [creatorFilter, setCreatorFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [onlyUnread, setOnlyUnread] = useState(false);
  const [selected, setSelected] = useState(null); // {creator_id, creator_name, fan_id, fan_name}
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [reply, setReply] = useState("");
  const [ppvPrice, setPpvPrice] = useState(0);
  const [lockedText, setLockedText] = useState(false);
  const [attached, setAttached] = useState([]); // [{id, type, thumb}]
  const [vaultOpen, setVaultOpen] = useState(false);
  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionQuery, setMentionQuery] = useState("");
  const [mentionResults, setMentionResults] = useState([]);
  const [mentionLoading, setMentionLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [notifPermission, setNotifPermission] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "denied"
  );
  const [soundOn, setSoundOn] = useState(true);
  const [recentEvents, setRecentEvents] = useState([]); // últimos ~15 eventos (msgs+ventas)
  const [showEvents, setShowEvents] = useState(true);
  const [nowTick, setNowTick] = useState(Date.now()); // se refresca cada 20s para recalcular "hace N min"
  const [dismissedUnanswered, setDismissedUnanswered] = useState(() => {
    // Set persistente en localStorage: keys `${creator_id}-${fan_id}-${message_id}` marcadas como leídas
    try {
      const raw = localStorage.getItem("chatterly_dismissed_unanswered_v1");
      return new Set(raw ? JSON.parse(raw) : []);
    } catch { return new Set(); }
  });
  const timerRef = useRef(null);
  const seenIdsRef = useRef(new Set()); // message_ids already alerted
  const firstLoadRef = useRef(true);

  const dismissUnanswered = (m) => {
    const key = `${m.creator_id}-${m.fan_id}-${m.message_id}`;
    setDismissedUnanswered((prev) => {
      const n = new Set(prev);
      n.add(key);
      try { localStorage.setItem("chatterly_dismissed_unanswered_v1", JSON.stringify([...n])); } catch { /* storage full */ }
      return n;
    });
  };

  const beep = useCallback(() => {
    if (!soundOn) return;
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const master = ctx.createGain();
      master.gain.value = 0.9; // volumen alto
      master.connect(ctx.destination);

      // Tri-tone estilo iPhone — 3 notas ascendentes rápidas con timbre brillante
      const notes = [
        { freq: 1046.5, at: 0.00, dur: 0.14 }, // C6
        { freq: 1318.5, at: 0.09, dur: 0.14 }, // E6
        { freq: 1568.0, at: 0.18, dur: 0.24 }, // G6 (sostenida más)
      ];

      const now = ctx.currentTime;
      notes.forEach((n) => {
        // Fundamental
        const o1 = ctx.createOscillator();
        o1.type = "sine";
        o1.frequency.value = n.freq;
        // Harmonic (octave up) para brillo tipo campanita iPhone
        const o2 = ctx.createOscillator();
        o2.type = "triangle";
        o2.frequency.value = n.freq * 2;

        const g = ctx.createGain();
        g.gain.setValueAtTime(0.0001, now + n.at);
        g.gain.exponentialRampToValueAtTime(0.7, now + n.at + 0.01);
        g.gain.exponentialRampToValueAtTime(0.0001, now + n.at + n.dur);

        const gh = ctx.createGain();
        gh.gain.setValueAtTime(0.0001, now + n.at);
        gh.gain.exponentialRampToValueAtTime(0.25, now + n.at + 0.01);
        gh.gain.exponentialRampToValueAtTime(0.0001, now + n.at + n.dur * 0.75);

        o1.connect(g).connect(master);
        o2.connect(gh).connect(master);
        o1.start(now + n.at);
        o2.start(now + n.at);
        o1.stop(now + n.at + n.dur + 0.02);
        o2.stop(now + n.at + n.dur + 0.02);
      });

      // Cerrar el contexto tras la reproducción para liberar recursos
      setTimeout(() => { try { ctx.close(); } catch (_) { /* noop */ } }, 900);
    } catch (e) { /* audio unavailable — ignore */ }
  }, [soundOn]);

  const pushEvent = useCallback((ev) => {
    setRecentEvents((prev) => {
      // Dedupe por id
      if (prev.some((e) => e.id === ev.id)) return prev;
      return [ev, ...prev].slice(0, 15);
    });
  }, []);

  const fireNotification = useCallback((msg) => {
    // Toast siempre
    toast.info(
      `${msg.fan_name || msg.fan_username || "Fan"} → ${msg.creator_name}`,
      { description: (msg.text || "").slice(0, 140) }
    );
    beep();
    pushEvent({
      id: `msg_${msg.message_id}`,
      kind: "message",
      title: `${msg.fan_name || msg.fan_username || "Fan"}`,
      subtitle: (msg.text || "").replace(/<[^>]+>/g, "").slice(0, 60),
      creator_name: msg.creator_name,
      creator_id: msg.creator_id,
      fan_id: msg.fan_id,
      fan_avatar: msg.fan_avatar,
      created_at: msg.created_at || new Date().toISOString(),
    });
    // Notificación nativa del sistema si el usuario dio permiso
    if (typeof Notification !== "undefined" && Notification.permission === "granted") {
      try {
        const n = new Notification(
          `${msg.fan_name || "Fan"} → ${msg.creator_name}`,
          {
            body: (msg.text || "").replace(/<[^>]+>/g, "").slice(0, 180),
            icon: msg.fan_avatar,
            tag: `chatterly-${msg.creator_id}-${msg.fan_id}`,
            renotify: true,
          }
        );
        n.onclick = () => {
          window.focus();
          n.close();
        };
      } catch (e) { /* notification failed — silently ignore */ }
    }
  }, [beep, pushEvent]);

  const fireSalesNotification = useCallback((n) => {
    const emoji = n.type?.includes("tip") ? "💸" : n.type?.includes("subscrib") || n.type?.includes("renew") ? "⭐" : "💰";
    const label = n.type?.includes("tip") ? "TIP" : n.type?.includes("subscrib") || n.type?.includes("renew") ? "Sub" : "Venta";
    const title = `${emoji} ${label} $${n.amount || 0} → ${n.creator_name}`;
    const desc = `${n.fan_name || "Anon"}${n.text ? " · " + n.text.replace(/<[^>]+>/g, "").slice(0, 100) : ""}`;
    toast.success(title, { description: desc });
    beep();
    pushEvent({
      id: `sale_${n.id}`,
      kind: n.type?.includes("tip") ? "tip" : n.type?.includes("subscrib") || n.type?.includes("renew") ? "sub" : "sale",
      title,
      subtitle: desc,
      amount: n.amount,
      creator_name: n.creator_name,
      creator_id: n.creator_id,
      fan_id: n.fan_id,
      fan_avatar: n.fan_avatar,
      created_at: n.created_at || new Date().toISOString(),
    });
    if (typeof Notification !== "undefined" && Notification.permission === "granted") {
      try {
        const nn = new Notification(title, {
          body: desc,
          icon: n.fan_avatar,
          tag: `chatterly-sale-${n.id}`,
        });
        nn.onclick = () => { window.focus(); nn.close(); };
      } catch (e) { /* ignore */ }
    }
  }, [beep, pushEvent]);

  const requestPermission = async () => {
    if (typeof Notification === "undefined") {
      toast.error("Tu navegador no soporta notificaciones del sistema");
      return;
    }
    const p = await Notification.requestPermission();
    setNotifPermission(p);
    if (p === "granted") {
      toast.success("Notificaciones activadas — ¡te avisaremos cuando llegue un mensaje!");
    } else {
      toast.warning("Notificaciones bloqueadas. Puedes activarlas en la configuración del navegador.");
    }
  };

  const load = useCallback(async (opts = {}) => {
    if (!opts.silent) setPolling(true);
    try {
      const r = await axios.get(`${API}/chatterly/messages/live?limit_per_creator=30`, {
        withCredentials: true,
      });
      const newMsgs = r.data.messages || [];
      if (!firstLoadRef.current) {
        const freshMsgs = newMsgs.filter(
          (m) => m.is_from_fan && m.message_id && !seenIdsRef.current.has(m.message_id)
        );
        freshMsgs.slice(0, 3).forEach(fireNotification);
      } else {
        // Primera carga: poblar el feed de Novedades con los últimos 8 mensajes de fans
        // (sin sonido ni toast — solo para dar contexto visual desde el arranque).
        newMsgs
          .filter((m) => m.is_from_fan && m.message_id)
          .slice(0, 8)
          .forEach((m) =>
            pushEvent({
              id: `msg_${m.message_id}`,
              kind: "message",
              title: m.fan_name || m.fan_username || "Fan",
              subtitle: (m.text || "").replace(/<[^>]+>/g, "").slice(0, 60),
              creator_name: m.creator_name,
              creator_id: m.creator_id,
              fan_id: m.fan_id,
              fan_avatar: m.fan_avatar,
              created_at: m.created_at || new Date().toISOString(),
            })
          );
      }
      newMsgs.forEach((m) => { if (m.message_id) seenIdsRef.current.add(m.message_id); });
      firstLoadRef.current = false;
      setMessages(newMsgs);
      setLastRefresh(new Date());
    } catch (e) {
      if (!opts.silent) toast.error("Error al obtener mensajes");
    } finally {
      if (!opts.silent) setPolling(false);
      setLoading(false);
    }
  }, [fireNotification, pushEvent]);

  // Poll notifications in parallel (slower — every 60s)
  const loadNotifications = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/chatterly/notifications/live`, {
        withCredentials: true,
        timeout: 60000,
      });
      const newNotifs = r.data?.notifications || [];
      const fresh = newNotifs.filter((n) => n.id && !seenIdsRef.current.has(`n_${n.id}`));
      if (!firstLoadRef.current) {
        fresh.slice(0, 5).forEach(fireSalesNotification);
      } else {
        // Primera carga: poblar el feed de Novedades con ventas/tips/subs recientes (sin sonido).
        fresh.slice(0, 7).forEach((n) =>
          pushEvent({
            id: `sale_${n.id}`,
            kind: n.type?.includes("tip") ? "tip" : n.type?.includes("subscrib") || n.type?.includes("renew") ? "sub" : "sale",
            title: `${n.type?.includes("tip") ? "💸 TIP" : n.type?.includes("subscrib") || n.type?.includes("renew") ? "⭐ Sub" : "💰 Venta"} $${n.amount || 0} → ${n.creator_name}`,
            subtitle: `${n.fan_name || "Anon"}${n.text ? " · " + n.text.replace(/<[^>]+>/g, "").slice(0, 60) : ""}`,
            amount: n.amount,
            creator_name: n.creator_name,
            creator_id: n.creator_id,
            fan_id: n.fan_id,
            fan_avatar: n.fan_avatar,
            created_at: n.created_at || new Date().toISOString(),
          })
        );
      }
      newNotifs.forEach((n) => { if (n.id) seenIdsRef.current.add(`n_${n.id}`); });
    } catch (e) {
      /* silent */
    }
  }, [fireSalesNotification, pushEvent]);

  const historyCacheRef = useRef(new Map()); // key: `${creator_id}-${fan_id}` → messages
  const prefetchQueueRef = useRef({ running: false, cancel: false });

  // Pre-carga en segundo plano de los historiales visibles para que abrir un chat sea instantáneo.
  // Corre después de cada refresh de mensajes, respetando el rate-limit de Chatterly (~1 req/500ms).
  const prefetchHistories = useCallback(async (msgs) => {
    if (prefetchQueueRef.current.running) return;
    prefetchQueueRef.current.running = true;
    prefetchQueueRef.current.cancel = false;
    try {
      const targets = msgs.slice(0, 40).filter((m) => {
        const key = `${m.creator_id}-${m.fan_id}`;
        return m.creator_id && m.fan_id && !historyCacheRef.current.has(key);
      });
      for (const m of targets) {
        if (prefetchQueueRef.current.cancel) break;
        const key = `${m.creator_id}-${m.fan_id}`;
        try {
          const r = await axios.get(
            `${API}/chatterly/messages/history?creator_id=${encodeURIComponent(m.creator_id)}&fan_id=${encodeURIComponent(m.fan_id)}`,
            { withCredentials: true, timeout: 15000 }
          );
          historyCacheRef.current.set(key, r.data.messages || []);
        } catch {
          /* silent — no bloqueamos flujo si un fan falla */
        }
        // Pequeña pausa para no saturar el rate-limit (60 req/min de Chatterly)
        await new Promise((res) => setTimeout(res, 500));
      }
    } finally {
      prefetchQueueRef.current.running = false;
    }
  }, []);

  useEffect(() => {
    load();
    // Notificaciones se lanzan aparte + con más espacio (cada 60s, endpoint más pesado)
    const initTimer = setTimeout(() => loadNotifications(), 3000);
    const notifTimer = setInterval(() => loadNotifications(), 60000);
    timerRef.current = setInterval(() => load({ silent: true }), POLL_MS);
    return () => {
      clearInterval(timerRef.current);
      clearInterval(notifTimer);
      clearTimeout(initTimer);
      prefetchQueueRef.current.cancel = true;
    };
  }, [load, loadNotifications]);

  // Después de cada refresh de la lista, pre-carga los historiales en segundo plano.
  useEffect(() => {
    if (messages.length === 0) return;
    const t = setTimeout(() => prefetchHistories(messages), 1200);
    return () => clearTimeout(t);
  }, [messages, prefetchHistories]);

  // Tick cada 20s para recalcular "hace N min" en el badge de sin-responder.
  useEffect(() => {
    const t = setInterval(() => setNowTick(Date.now()), 20000);
    return () => clearInterval(t);
  }, []);

  // Buscar creadores mencionables con debounce (@tag en mensajes)
  useEffect(() => {
    if (!mentionOpen || !selected) return;
    setMentionLoading(true);
    const t = setTimeout(async () => {
      try {
        const params = new URLSearchParams({ creator_id: selected.creator_id });
        if (mentionQuery) params.set("query", mentionQuery);
        const r = await axios.get(`${API}/chatterly/mentions?${params.toString()}`, { withCredentials: true });
        setMentionResults(r.data?.mentions || []);
      } catch { setMentionResults([]); }
      finally { setMentionLoading(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [mentionOpen, mentionQuery, selected]);

  const insertMention = (m) => {
    const tag = `@${m.username || m.name}`;
    setReply((r) => (r ? `${r} ${tag} ` : `${tag} `));
    setMentionOpen(false);
    setMentionQuery("");
  };

  const openConversation = async (m) => {
    const cacheKey = `${m.creator_id}-${m.fan_id}`;
    // Auto-marcar como leído en la lista de sin responder al abrir la conversación
    if (m.message_id) {
      dismissUnanswered(m);
    }
    // 1) Cambiar selección INMEDIATAMENTE (renderizado optimista)
    setSelected({
      creator_id: m.creator_id,
      creator_name: m.creator_name,
      fan_id: m.fan_id,
      fan_name: m.fan_name || m.fan_username || "Fan",
      fan_avatar: m.fan_avatar,
      modelo: m.modelo,
    });
    // 2) Si hay cache, mostrar de una y refrescar en background
    const cached = historyCacheRef.current.get(cacheKey);
    if (cached) {
      setHistory(cached);
      setHistoryLoading(false);
      // Refresh silencioso — no bloquea UI
      axios.get(`${API}/chatterly/messages/history?creator_id=${encodeURIComponent(m.creator_id)}&fan_id=${encodeURIComponent(m.fan_id)}`, { withCredentials: true })
        .then((r) => {
          const msgs = r.data.messages || [];
          historyCacheRef.current.set(cacheKey, msgs);
          setHistory(msgs);
        }).catch(() => { /* silent */ });
      return;
    }
    // 3) Sin cache: fetch, pero mostrando placeholder mínimo derivado del `lastMessage`
    setHistory(m.text ? [{
      id: `optimistic-${m.message_id}`,
      text: m.text,
      created_at: m.created_at,
      is_from_fan: m.is_from_fan,
      is_tip: m.is_tip,
      price: 0,
      locked_text: m.locked,
    }] : []);
    setHistoryLoading(true);
    try {
      const r = await axios.get(
        `${API}/chatterly/messages/history?creator_id=${encodeURIComponent(m.creator_id)}&fan_id=${encodeURIComponent(m.fan_id)}`,
        { withCredentials: true }
      );
      const msgs = r.data.messages || [];
      historyCacheRef.current.set(cacheKey, msgs);
      setHistory(msgs);
    } catch (e) {
      toast.error("Error al cargar historial");
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleSend = async () => {
    if (!selected) return;
    const hasContent = reply.trim() || attached.length > 0;
    if (!hasContent) return;
    const priceNum = parseFloat(ppvPrice) || 0;
    const confirmMsg =
      `¿ENVIAR como ${selected.creator_name} → ${selected.fan_name}?\n\n` +
      (reply ? `Texto: "${reply.slice(0, 150)}"\n` : "") +
      (attached.length ? `Media: ${attached.length} archivo(s)\n` : "") +
      (priceNum > 0 ? `💰 PPV: $${priceNum}\n` : "") +
      (lockedText ? "🔒 Texto bloqueado (visible sólo tras compra)" : "");
    if (!window.confirm(confirmMsg)) return;
    setSending(true);
    try {
      await axios.post(
        `${API}/chatterly/messages/send`,
        {
          creator_id: selected.creator_id,
          fan_id: String(selected.fan_id),
          text: reply,
          price: priceNum,
          media_files: attached.map((a) => Number(a.id)).filter((n) => Number.isFinite(n)),
          previews: [],
          locked_text: lockedText,
        },
        { withCredentials: true }
      );
      toast.success("Mensaje enviado ✓");
      setReply("");
      setPpvPrice(0);
      setLockedText(false);
      setAttached([]);
      await openConversation({ creator_id: selected.creator_id, fan_id: selected.fan_id, creator_name: selected.creator_name, fan_name: selected.fan_name });
      load({ silent: true });
    } catch (err) {
      // FastAPI puede devolver detail como string O como array de errores de Pydantic — normalizar
      const d = err.response?.data?.detail;
      const msg = typeof d === "string"
        ? d
        : Array.isArray(d)
          ? d.map((x) => (x?.msg || JSON.stringify(x))).join(" · ")
          : (err.message || "Error al enviar");
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  // Creator dropdown values
  const creatorOptions = Array.from(
    messages.reduce((acc, m) => {
      acc.set(m.creator_id, m.creator_name);
      return acc;
    }, new Map())
  );

  const TWO_MIN = 2 * 60 * 1000;

  // Determina cuánto lleva un mensaje del fan sin responder. Devuelve minutos o null.
  const unansweredMinutes = (m) => {
    if (!m.is_from_fan || !m.created_at) return null;
    const ts = new Date(m.created_at).getTime();
    if (isNaN(ts)) return null;
    const age = nowTick - ts;
    if (age < TWO_MIN) return null;
    return Math.floor(age / 60000);
  };

  const filtered = messages.filter((m) => {
    if (creatorFilter !== "all" && m.creator_id !== creatorFilter) return false;
    if (onlyUnread && !m.unread_count) return false;
    if (search) {
      const q = search.toLowerCase();
      const hay = `${m.creator_name} ${m.fan_name} ${m.fan_username} ${m.text}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  // Separa mensajes SIN RESPONDER (>2 min, no marcados como leídos) al top
  const unansweredList = [];
  const restList = [];
  for (const m of filtered) {
    const mins = unansweredMinutes(m);
    const dismissKey = `${m.creator_id}-${m.fan_id}-${m.message_id}`;
    if (mins != null && !dismissedUnanswered.has(dismissKey)) {
      unansweredList.push({ ...m, _waitingMin: mins });
    } else {
      restList.push(m);
    }
  }
  unansweredList.sort((a, b) => b._waitingMin - a._waitingMin);

  return (
    <div className="h-full min-h-0 overflow-hidden grid gap-3 grid-cols-1 lg:grid-cols-[280px_1fr_320px]" style={{ gridTemplateRows: "minmax(0, 1fr)" }} data-testid="chatterly-live-panel">
      {/* LEFT — conversations list (Chatterly style) */}
      <div className={`${selected ? "hidden lg:flex" : "flex"} rounded-2xl border border-white/10 bg-black/40 flex-col overflow-hidden min-h-0`}>
        {/* Filters bar top */}
        <div className="p-3 border-b border-white/5 flex items-center gap-2 flex-shrink-0">
          <select
            value={creatorFilter}
            onChange={(e) => setCreatorFilter(e.target.value)}
            data-testid="chatterly-live-creator-filter"
            className="flex-1 px-2 py-1 rounded-lg bg-zinc-900 border border-white/10 text-white text-xs"
          >
            <option value="all">All Creators</option>
            {creatorOptions.map(([cid, name]) => (
              <option key={cid} value={cid}>{name}</option>
            ))}
          </select>
          <button
            onClick={() => setSoundOn((v) => !v)}
            className={`p-1.5 rounded-lg ${soundOn ? "text-emerald-400" : "text-gray-500"}`}
            title={soundOn ? "Silenciar" : "Activar sonido"}
          >
            {soundOn ? <Volume2 className="w-3.5 h-3.5" /> : <VolumeX className="w-3.5 h-3.5" />}
          </button>
          <button
            onClick={requestPermission}
            className={`p-1.5 rounded-lg ${
              notifPermission === "granted" ? "text-emerald-400" : notifPermission === "denied" ? "text-red-400" : "text-amber-400"
            }`}
            title="Notificaciones del sistema"
          >
            {notifPermission === "granted" ? <Bell className="w-3.5 h-3.5" /> : <BellOff className="w-3.5 h-3.5" />}
          </button>
          <button onClick={() => load()} disabled={polling} title="Refrescar">
            <RefreshCw className={`w-3.5 h-3.5 text-gray-400 ${polling ? "animate-spin" : ""}`} />
          </button>
        </div>

        {/* Search */}
        <div className="p-3 border-b border-white/5 flex-shrink-0">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search conversations..."
              className="w-full pl-8 pr-2 py-2 rounded-lg bg-zinc-900 border border-white/10 text-white text-xs placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
            />
          </div>
          <label className="mt-2 flex items-center gap-1 text-[10px] text-gray-300 cursor-pointer">
            <input type="checkbox" checked={onlyUnread} onChange={(e) => setOnlyUnread(e.target.checked)} className="accent-emerald-500" />
            Solo no leídos
          </label>
        </div>

        {/* Feed de novedades — mensajes nuevos + ventas + tips + subs en tiempo real */}
        {recentEvents.length > 0 && (
          <div className="border-b border-white/5 bg-gradient-to-b from-emerald-500/5 to-transparent flex-shrink-0">
            <button
              onClick={() => setShowEvents((v) => !v)}
              className="w-full px-3 py-2 flex items-center justify-between text-[10px] uppercase tracking-widest text-emerald-400 hover:bg-white/[0.02]"
              data-testid="chatterly-events-toggle"
            >
              <span className="flex items-center gap-1.5">
                <Bell className="w-3 h-3 animate-pulse" />
                Novedades ({recentEvents.length})
              </span>
              <span className="text-gray-500">{showEvents ? "▾" : "▸"}</span>
            </button>
            {showEvents && (
              <div className="max-h-52 overflow-y-auto px-1 pb-2 space-y-0.5">
                {recentEvents.map((ev) => {
                  const isSale = ev.kind !== "message";
                  return (
                    <button
                      key={ev.id}
                      onClick={() => {
                        if (ev.creator_id && ev.fan_id) {
                          openConversation({
                            creator_id: ev.creator_id,
                            fan_id: ev.fan_id,
                            creator_name: ev.creator_name,
                            fan_name: ev.title,
                            fan_avatar: ev.fan_avatar,
                          });
                        }
                      }}
                      className={`w-full text-left px-2 py-1.5 rounded flex items-start gap-2 text-[11px] hover:bg-white/5 transition ${
                        isSale ? "text-amber-300" : "text-gray-300"
                      }`}
                      data-testid={`chatterly-event-${ev.id}`}
                    >
                      <span className="text-sm leading-none flex-shrink-0 mt-0.5">
                        {ev.kind === "tip" ? "💸" : ev.kind === "sub" ? "⭐" : ev.kind === "sale" ? "💰" : "💬"}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">{ev.title}</div>
                        {ev.subtitle && (
                          <div className="text-gray-500 truncate text-[10px]">{ev.subtitle}</div>
                        )}
                        <div className="text-gray-600 text-[9px]">
                          {ev.creator_name} · {ev.created_at ? new Date(ev.created_at).toLocaleTimeString("es-CO", { hour: "2-digit", minute: "2-digit" }) : ""}
                        </div>
                      </div>
                    </button>
                  );
                })}
                <button
                  onClick={() => setRecentEvents([])}
                  className="w-full text-[9px] text-gray-500 hover:text-gray-300 py-1"
                >
                  Limpiar novedades
                </button>
              </div>
            )}
          </div>
        )}

        {/* Conversations list */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="py-12 text-center text-gray-500 text-xs">Cargando…</div>
          ) : filtered.length === 0 ? (
            <div className="py-12 text-center text-gray-500 text-xs">Sin conversaciones.</div>
          ) : (
            <>
              {/* Sección PIN: sin responder >2min, ordenados por tiempo esperando */}
              {unansweredList.length > 0 && (
                <div className="border-b border-red-500/20 bg-gradient-to-b from-red-500/5 to-transparent">
                  <div className="px-3 py-1.5 text-[10px] uppercase tracking-widest text-red-400 flex items-center gap-1.5 font-semibold">
                    <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
                    Sin responder ({unansweredList.length})
                  </div>
                  {unansweredList.map((m) => {
                    const isSel = selected?.creator_id === m.creator_id && selected?.fan_id === m.fan_id;
                    const urgent = m._waitingMin >= 5;
                    return (
                      <div
                        key={`unans-${m.creator_id}-${m.fan_id}-${m.message_id}`}
                        className={`relative group border-b border-white/5 transition ${
                          isSel ? "bg-emerald-500/10 border-l-2 border-l-emerald-500" : urgent ? "bg-red-500/5 hover:bg-red-500/10 border-l-2 border-l-red-500" : "bg-amber-500/5 hover:bg-amber-500/10 border-l-2 border-l-amber-500"
                        }`}
                      >
                        <button
                          data-testid={`chatterly-unanswered-${m.creator_id}-${m.fan_id}`}
                          onClick={() => openConversation(m)}
                          className="w-full text-left px-3 py-2.5"
                        >
                          <div className="flex items-start gap-2.5">
                            {m.fan_avatar ? (
                              <img src={m.fan_avatar} alt="" className="w-9 h-9 rounded-full object-cover flex-shrink-0" onError={(e) => (e.target.style.display = "none")} />
                            ) : (
                              <div className="w-9 h-9 rounded-full bg-zinc-800 flex items-center justify-center flex-shrink-0 text-sm">👤</div>
                            )}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between gap-1">
                                <div className="text-white text-xs font-medium truncate">
                                  {m.fan_name || `@${m.fan_username}`}
                                </div>
                                <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-bold flex-shrink-0 mr-6 ${
                                  urgent ? "bg-red-500 text-white" : "bg-amber-500 text-black"
                                }`}>
                                  ⏰ {m._waitingMin}m
                                </span>
                              </div>
                              <div className="text-[10px] text-gray-300 truncate mb-0.5">
                                via {m.modelo?.name || m.creator_name}
                              </div>
                              <div className="text-[11px] text-gray-200 truncate" dangerouslySetInnerHTML={{ __html: (m.text || "").slice(0, 80) }} />
                            </div>
                          </div>
                        </button>
                        {/* Marcar como leído */}
                        <button
                          onClick={(e) => { e.stopPropagation(); dismissUnanswered(m); }}
                          data-testid={`chatterly-unanswered-dismiss-${m.creator_id}-${m.fan_id}`}
                          title="Marcar como leído (ocultar de sin responder)"
                          className="absolute top-1.5 right-1.5 opacity-0 group-hover:opacity-100 transition p-1 rounded-md bg-black/60 hover:bg-emerald-500/80 text-gray-200 hover:text-white"
                        >
                          <Check className="w-3 h-3" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Resto de conversaciones (respondidas o < 2min) */}
              {restList.map((m) => {
                const isSel = selected?.creator_id === m.creator_id && selected?.fan_id === m.fan_id;
                return (
                  <button
                    key={`${m.creator_id}-${m.fan_id}-${m.message_id}`}
                    data-testid={`chatterly-live-msg-${m.creator_id}-${m.fan_id}`}
                    onClick={() => openConversation(m)}
                    className={`w-full text-left px-3 py-2.5 border-b border-white/5 hover:bg-white/[0.03] transition ${
                      isSel ? "bg-emerald-500/10 border-l-2 border-l-emerald-500" : ""
                    }`}
                  >
                    <div className="flex items-start gap-2.5">
                      {m.fan_avatar ? (
                        <img src={m.fan_avatar} alt="" className="w-9 h-9 rounded-full object-cover flex-shrink-0" onError={(e) => (e.target.style.display = "none")} />
                      ) : (
                        <div className="w-9 h-9 rounded-full bg-zinc-800 flex items-center justify-center flex-shrink-0 text-sm">👤</div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-1">
                          <div className="text-white text-xs font-medium truncate">
                            {m.fan_name || `@${m.fan_username}`}
                          </div>
                          <span className="text-[9px] text-gray-500 flex-shrink-0">
                            {m.created_at ? new Date(m.created_at).toLocaleString("es-CO", { hour: "2-digit", minute: "2-digit", day: "2-digit", month: "2-digit" }) : ""}
                          </span>
                        </div>
                        <div className="text-[10px] text-gray-500 truncate mb-0.5">
                          via {m.modelo?.name || m.creator_name}
                        </div>
                        <div className="text-[11px] text-gray-400 truncate" dangerouslySetInnerHTML={{ __html: (m.text || "").slice(0, 80) }} />
                        {m.unread_count > 0 && (
                          <span className="mt-1 inline-block px-1.5 py-0.5 rounded-full bg-emerald-500 text-black text-[9px] font-bold">
                            {m.unread_count}
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </>
          )}
        </div>
      </div>

      {/* CENTER — chat */}
      <div className={`${selected ? "flex" : "hidden lg:flex"} rounded-2xl border border-white/10 bg-black/40 flex-col overflow-hidden min-h-0`}>
        {!selected ? (
          <div className="flex-1 flex items-center justify-center text-gray-300 text-sm">
            Selecciona una conversación
          </div>
        ) : (
          <>
            <div className="px-4 py-3 border-b border-white/5 flex items-center gap-3 flex-shrink-0">
              <button
                onClick={() => setSelected(null)}
                className="lg:hidden text-gray-300 hover:text-white p-1 -ml-1"
                data-testid="chatterly-back-to-list"
                title="Volver a la lista"
              >
                <ArrowLeft className="w-4 h-4" />
              </button>
              {selected.fan_avatar ? (
                <img src={selected.fan_avatar} alt="" className="w-8 h-8 rounded-full object-cover" onError={(e) => (e.target.style.display = "none")} />
              ) : (
                <div className="w-8 h-8 rounded-full bg-zinc-800 flex items-center justify-center text-xs">👤</div>
              )}
              <div className="flex-1 min-w-0">
                <div className="text-white text-sm font-semibold truncate">{selected.fan_name}</div>
                <ChatHeaderSeen selected={selected} fallback={selected.modelo?.name || selected.creator_name} />
              </div>
              <button onClick={() => setSelected(null)} className="hidden lg:block text-gray-300 hover:text-white">
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Messages */}
            <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-2" data-testid="chatterly-history-scroll">
              {historyLoading ? (
                <div className="text-center text-gray-300 text-xs py-6">Cargando historial…</div>
              ) : history.length === 0 ? (
                <div className="text-center text-gray-300 text-xs py-6">Sin historial.</div>
              ) : (
                (() => {
                  const ordered = [...history].reverse();
                  const rows = [];
                  let lastDate = null;
                  const fmtDay = (d) => {
                    const day = new Date(d);
                    const now = new Date();
                    const sameDay = day.toDateString() === now.toDateString();
                    const yest = new Date(now); yest.setDate(now.getDate() - 1);
                    const isYest = day.toDateString() === yest.toDateString();
                    if (sameDay) return "Hoy";
                    if (isYest) return "Ayer";
                    return day.toLocaleDateString("es-CO", { month: "short", day: "numeric", year: day.getFullYear() !== now.getFullYear() ? "numeric" : undefined });
                  };
                  ordered.forEach((h) => {
                    const key = h.created_at ? new Date(h.created_at).toDateString() : "unknown";
                    if (key !== lastDate) {
                      rows.push(
                        <div key={`sep-${key}-${h.id}`} className="flex items-center gap-3 my-3">
                          <div className="flex-1 h-px bg-white/5" />
                          <span className="text-[10px] uppercase tracking-widest text-gray-400 font-semibold">
                            {h.created_at ? fmtDay(h.created_at) : "—"}
                          </span>
                          <div className="flex-1 h-px bg-white/5" />
                        </div>
                      );
                      lastDate = key;
                    }
                    rows.push(
                      <div key={h.id} className={`flex ${h.is_from_fan ? "justify-start" : "justify-end"}`}>
                        <div className={`max-w-[72%] text-sm leading-relaxed shadow-lg overflow-hidden ${
                          h.is_from_fan
                            ? "bg-gradient-to-br from-zinc-800 to-zinc-900 text-white rounded-3xl rounded-tl-md border-l-2 border-l-fuchsia-500"
                            : "bg-gradient-to-br from-amber-400 to-amber-500 text-zinc-900 font-medium rounded-3xl rounded-tr-md"
                        }`}>
                          {/* Media attachments (fotos/videos/gifs) */}
                          {h.media_items && h.media_items.length > 0 && (
                            <div className={`grid ${h.media_items.length === 1 ? "grid-cols-1" : "grid-cols-2"} gap-0.5 max-w-[280px]`}>
                              {h.media_items.slice(0, 4).map((mm) => {
                                const proxyUrl = `${API}/chatterly/media-proxy?creator_id=${encodeURIComponent(selected.creator_id)}&url=${encodeURIComponent(mm.url)}`;
                                return (
                                  <div key={mm.id} className="relative aspect-square bg-black/40">
                                    <img
                                      src={proxyUrl}
                                      alt=""
                                      loading="lazy"
                                      className="w-full h-full object-cover"
                                      onError={(e) => {
                                        e.target.style.display = "none";
                                        const ph = e.target.parentElement.querySelector('[data-media-fallback]');
                                        if (ph) ph.classList.remove("hidden");
                                      }}
                                    />
                                    <div
                                      data-media-fallback
                                      className="hidden absolute inset-0 bg-gradient-to-br from-zinc-900 to-zinc-800 flex flex-col items-center justify-center gap-1"
                                    >
                                      <span className="text-2xl">{mm.type === "video" ? "🎥" : mm.type === "gif" ? "🎞️" : "🖼️"}</span>
                                      <span className="text-[9px] uppercase tracking-widest text-gray-400">{mm.type}</span>
                                    </div>
                                    {mm.type === "video" && (
                                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                        <div className="w-8 h-8 rounded-full bg-black/60 flex items-center justify-center">
                                          <span className="text-white text-sm">▶</span>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                              {h.media_items.length > 4 && (
                                <div className="absolute bottom-1 right-1 bg-black/70 text-white text-[10px] px-1.5 py-0.5 rounded-full">
                                  +{h.media_items.length - 4}
                                </div>
                              )}
                            </div>
                          )}
                          {/* Texto + metadatos */}
                          <div className="px-4 py-2.5">
                            {(h.text || h.media_items?.length === 0) && (
                              <div dangerouslySetInnerHTML={{ __html: h.text || "(sin texto)" }} />
                            )}
                            <div className={`text-[10px] mt-1.5 flex items-center gap-1.5 ${h.is_from_fan ? "text-gray-400" : "text-zinc-800/80 justify-end"}`}>
                              <span>
                                {h.created_at ? new Date(h.created_at).toLocaleTimeString("es-CO", { hour: "2-digit", minute: "2-digit" }) : ""}
                              </span>
                              {h.is_tip && <span className="font-bold">· 💸 TIP ${h.price}</span>}
                              {h.price > 0 && !h.is_tip && <span className="font-bold">· 💰 ${h.price}</span>}
                              {h.locked_text && <span>🔒</span>}
                              {h.is_opened && !h.is_from_fan && <span>✓</span>}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  });
                  return rows;
                })()
              )}
            </div>

            {/* Composer */}
            <div className="border-t border-white/5 flex-shrink-0 bg-black/60 backdrop-blur-sm">
              {/* Top action row (AI Reply + Script) */}
              <div className="px-3 pt-2 flex items-center gap-2">
                <button
                  data-testid="composer-ai-reply"
                  onClick={() => toast.info("AI Reply próximamente")}
                  className="ml-auto inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-gradient-to-r from-fuchsia-500 to-purple-600 text-white text-[10px] font-semibold hover:opacity-90"
                >
                  ✨ AI Reply
                </button>
                <button
                  data-testid="composer-script-btn"
                  onClick={() => toast.info("Scripts próximamente")}
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-white/5 border border-white/10 text-gray-200 text-[10px] font-semibold hover:bg-white/10"
                >
                  📄 Script
                </button>
              </div>

              {/* Attached chips */}
              {attached.length > 0 && (
                <div className="px-3 pt-2 flex flex-wrap gap-1.5">
                  {attached.map((a) => (
                    <div key={a.id} className="relative w-12 h-12 rounded-lg overflow-hidden border border-white/10">
                      {a.thumb ? <img src={a.thumb} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full bg-zinc-800 flex items-center justify-center">{a.type === "video" ? "🎥" : "🖼"}</div>}
                      <button onClick={() => setAttached((prev) => prev.filter((x) => x.id !== a.id))} className="absolute top-0 right-0 bg-black/70 rounded-bl p-0.5">
                        <X className="w-3 h-3 text-white" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Textarea principal */}
              <div className="px-3 pt-2">
                <textarea
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey && !e.altKey && !e.ctrlKey && !e.metaKey && !e.nativeEvent.isComposing) {
                      e.preventDefault();
                      if (!sending && (reply.trim() || attached.length > 0)) {
                        handleSend();
                      }
                    }
                  }}
                  data-testid="chatterly-live-reply"
                  placeholder="Type a message..."
                  rows={2}
                  className="w-full p-2.5 rounded-lg bg-zinc-900 border border-purple-500/40 focus:border-purple-500 text-white text-sm placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-purple-500 resize-none"
                />
              </div>

              {/* Quick emoji strip */}
              <div className="px-3 py-1.5 flex gap-1.5 overflow-x-auto scrollbar-hide">
                {["😊","🥰","😍","❤️","🔥","💕","😘","🍑","🍆","💦","🥵","😈","💋","👅"].map((e) => (
                  <button
                    key={e}
                    onClick={() => setReply((r) => r + e)}
                    className="text-lg hover:scale-125 transition"
                    data-testid={`composer-emoji-${e}`}
                  >
                    {e}
                  </button>
                ))}
              </div>

              {/* Toolbar inferior estilo OnlyFans */}
              <div className="relative px-3 py-2 border-t border-white/5 flex items-center gap-1">
                {/* Dropdown de @tag (mencionar creadores) */}
                {mentionOpen && (
                  <div
                    className="absolute bottom-full left-2 right-2 mb-2 rounded-xl bg-zinc-950 border border-fuchsia-500/40 shadow-2xl shadow-fuchsia-500/20 max-h-64 overflow-hidden flex flex-col"
                    data-testid="mention-dropdown"
                  >
                    <div className="p-2 border-b border-white/5 flex items-center gap-2">
                      <span className="text-fuchsia-400 font-bold text-sm">@</span>
                      <input
                        autoFocus
                        value={mentionQuery}
                        onChange={(e) => setMentionQuery(e.target.value)}
                        placeholder="Buscar creador por nombre o @username…"
                        data-testid="mention-search-input"
                        className="flex-1 bg-transparent text-white text-xs placeholder-gray-500 focus:outline-none"
                      />
                      <button
                        onClick={() => { setMentionOpen(false); setMentionQuery(""); }}
                        className="text-gray-400 hover:text-white p-0.5"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <div className="overflow-y-auto flex-1">
                      {mentionLoading && mentionResults.length === 0 ? (
                        <div className="py-4 text-center text-gray-400 text-xs">Buscando…</div>
                      ) : mentionResults.length === 0 ? (
                        <div className="py-4 text-center text-gray-400 text-xs">
                          {mentionQuery ? "Sin resultados" : "Escribe para buscar creadores"}
                        </div>
                      ) : (
                        mentionResults.map((m) => (
                          <button
                            key={m.id}
                            onClick={() => insertMention(m)}
                            data-testid={`mention-result-${m.id}`}
                            className="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-fuchsia-500/10 text-left transition"
                          >
                            {m.avatar ? (
                              <img src={m.avatar} alt="" className="w-8 h-8 rounded-full object-cover" onError={(e) => (e.target.style.display = "none")} />
                            ) : (
                              <div className="w-8 h-8 rounded-full bg-fuchsia-500/20 flex items-center justify-center text-xs font-bold text-fuchsia-300">
                                {(m.name || m.username || "?").slice(0, 1).toUpperCase()}
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <div className="text-white text-xs font-medium truncate">{m.name || m.username}</div>
                              <div className="text-fuchsia-400 text-[10px] truncate">@{m.username}</div>
                            </div>
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                )}
                <ToolBtn testid="tool-emoji" onClick={() => setReply((r) => r + "😊")} title="Emojis">
                  <span className="text-base">😊</span>
                </ToolBtn>
                <ToolBtn testid="tool-gallery" onClick={() => setVaultOpen(true)} title="Vault / Galería">
                  <span className="text-base">🖼️</span>
                </ToolBtn>
                <ToolBtn testid="tool-gif" onClick={() => toast.info("GIF picker próximamente")} title="GIF">
                  <span className="text-[10px] font-bold px-1 rounded bg-white/10">GIF</span>
                </ToolBtn>
                <ToolBtn testid="tool-tag-creator" onClick={() => setMentionOpen((v) => !v)} title="Mencionar otro creador (@)" active={mentionOpen}>
                  <span className="text-base font-bold">@</span>
                </ToolBtn>
                <ToolBtn testid="tool-pm" onClick={() => setLockedText((v) => !v)} title="Locked (PM)" active={lockedText}>
                  <Lock className="w-4 h-4" />
                </ToolBtn>
                <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-md border ${
                  Number(ppvPrice) > 0 ? "bg-emerald-500/10 border-emerald-500/40" : "bg-white/5 border-white/10"
                }`}>
                  <DollarSign className="w-3.5 h-3.5 text-emerald-400" />
                  <input
                    type="number"
                    min={0}
                    step={0.5}
                    value={ppvPrice}
                    onChange={(e) => setPpvPrice(e.target.value)}
                    data-testid="chatterly-live-ppv"
                    placeholder="0"
                    className="w-10 bg-transparent border-0 text-xs text-white focus:outline-none"
                  />
                </div>
                <ToolBtn testid="tool-schedule" onClick={() => toast.info("Programar mensaje próximamente")} title="Programar">
                  <span className="text-base">📅</span>
                </ToolBtn>
                <ToolBtn testid="tool-attach" onClick={() => setVaultOpen(true)} title="Adjuntar">
                  <Paperclip className="w-4 h-4" />
                </ToolBtn>
                <div className="ml-auto flex items-center gap-1.5">
                  <button
                    onClick={() => { setReply(""); setAttached([]); setPpvPrice(0); }}
                    data-testid="composer-clear"
                    disabled={!reply && attached.length === 0}
                    className="p-1.5 rounded hover:bg-white/5 text-gray-400 hover:text-red-400 disabled:opacity-30"
                    title="Limpiar"
                  >
                    🗑️
                  </button>
                  <button
                    onClick={handleSend}
                    disabled={sending || (!reply.trim() && attached.length === 0)}
                    data-testid="chatterly-live-send"
                    className="w-9 h-9 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 hover:opacity-90 text-black flex items-center justify-center disabled:opacity-40 flex-shrink-0 shadow-lg"
                    title="Enviar"
                  >
                    <Send className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <div className="px-3 pb-2 text-[9px] text-amber-400/80 text-right">
                ⚠️ Envía como {selected.creator_name}
              </div>
            </div>
          </>
        )}
      </div>

      {/* RIGHT — fan info panel (oculto en mobile — solo desktop) */}
      <div className="hidden lg:block min-h-0 overflow-hidden">
        <FanDetailPanel selected={selected} />
      </div>

      {/* Vault picker */}
      {vaultOpen && selected && (
        <VaultPickerModal
          creatorId={selected.creator_id}
          onClose={() => setVaultOpen(false)}
          onPick={(picked) => {
            setAttached((prev) => [...prev, ...picked.filter(p => !prev.find(x => x.id === p.id))]);
            setVaultOpen(false);
          }}
        />
      )}
    </div>
  );
}

// ==================== FAN DETAIL RIGHT PANEL ====================
// Cache global entre renders para info del fan
const fanDetailCache = new Map();

// Header dinámico: "Seen X hours ago" leído del cache de fan-detail
function ChatHeaderSeen({ selected, fallback }) {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick((v) => v + 1), 60000);
    return () => clearInterval(t);
  }, []);
  const key = `${selected.creator_id}-${selected.fan_id}`;
  const data = fanDetailCache.get(key);
  const lastSeen = data?.fan?.last_seen;
  const isOnline = data?.fan?.is_online;
  let seenLabel = null;
  if (isOnline) {
    seenLabel = "Online now";
  } else if (lastSeen) {
    const diffMs = Date.now() - new Date(lastSeen).getTime();
    if (!isNaN(diffMs) && diffMs > 0) {
      const mins = Math.floor(diffMs / 60000);
      if (mins < 1) seenLabel = "Seen just now";
      else if (mins < 60) seenLabel = `Seen ${mins} min ago`;
      else if (mins < 1440) {
        const h = Math.floor(mins / 60);
        seenLabel = `Seen ${h} hour${h === 1 ? "" : "s"} ago`;
      } else {
        const d = Math.floor(mins / 1440);
        seenLabel = `Seen ${d} day${d === 1 ? "" : "s"} ago`;
      }
    }
  }
  return (
    <div className="text-[11px] text-gray-300 truncate" data-tick={tick}>
      {fallback}
      {seenLabel && <span className={`ml-1.5 ${isOnline ? "text-emerald-400" : "text-gray-400"}`}>· {seenLabel}</span>}
    </div>
  );
}

function FanDetailPanel({ selected }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("insights"); // insights | ppvs | notes
  const [noteDraft, setNoteDraft] = useState("");
  const [sourceDraft, setSourceDraft] = useState("");
  const [countryDraft, setCountryDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [savedTs, setSavedTs] = useState(null);

  useEffect(() => {
    if (!selected) { setData(null); return; }
    const cacheKey = `${selected.creator_id}-${selected.fan_id}`;
    const cached = fanDetailCache.get(cacheKey);
    if (cached) {
      setData(cached);
      setLoading(false);
      setNoteDraft(cached?.notes?.note || "");
      setSourceDraft(cached?.notes?.source || "");
      setCountryDraft(cached?.fan?.country || "");
    } else {
      setData(null);
      setLoading(true);
      setNoteDraft("");
      setSourceDraft("");
      setCountryDraft("");
    }
    let alive = true;
    axios.get(
      `${API}/chatterly/fan-detail?creator_id=${encodeURIComponent(selected.creator_id)}&fan_id=${encodeURIComponent(selected.fan_id)}`,
      { withCredentials: true }
    ).then((r) => {
      if (!alive) return;
      fanDetailCache.set(cacheKey, r.data);
      setData(r.data);
      setNoteDraft(r.data?.notes?.note || "");
      setSourceDraft(r.data?.notes?.source || "");
      setCountryDraft(r.data?.fan?.country || "");
    }).catch(() => {
      if (!alive) return;
      if (!cached) setData(null);
    }).finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [selected]);

  // Autosave notes con debounce
  useEffect(() => {
    if (!selected || !data) return;
    const dirty = (noteDraft !== (data?.notes?.note || "")) ||
                  (sourceDraft !== (data?.notes?.source || "")) ||
                  (countryDraft !== (data?.fan?.country || ""));
    if (!dirty) return;
    const t = setTimeout(async () => {
      try {
        setSaving(true);
        await axios.post(`${API}/chatterly/fan-notes`, {
          creator_id: selected.creator_id,
          fan_id: selected.fan_id,
          note: noteDraft,
          source: sourceDraft,
          country: countryDraft,
        }, { withCredentials: true });
        // Actualizar cache
        const cacheKey = `${selected.creator_id}-${selected.fan_id}`;
        const cached = fanDetailCache.get(cacheKey);
        if (cached) {
          cached.notes = { ...(cached.notes || {}), note: noteDraft, source: sourceDraft };
          if (cached.fan) cached.fan.country = countryDraft;
          fanDetailCache.set(cacheKey, cached);
        }
        setSavedTs(Date.now());
      } catch { /* silent */ }
      finally { setSaving(false); }
    }, 800);
    return () => clearTimeout(t);
  }, [noteDraft, sourceDraft, countryDraft, selected, data]);

  if (!selected) {
    return (
      <div className="rounded-2xl border border-white/10 bg-black/40 flex items-center justify-center text-gray-300 text-xs p-4 h-full">
        Info del fan aparecerá aquí
      </div>
    );
  }

  const s = data?.spending || {};
  const f = data?.fan || {};
  const power = s.power || 0;
  const subDurationDays = f.subscribed_at
    ? Math.max(0, Math.floor((Date.now() - new Date(f.subscribed_at).getTime()) / 86400000))
    : null;

  return (
    <div className="rounded-2xl border border-white/10 bg-black/40 flex flex-col overflow-hidden h-full">
      {/* Header */}
      <div className="flex-shrink-0 px-3 py-2.5 border-b border-white/5 flex items-center gap-2">
        <h3 className="text-sm font-bold text-white">Fan Insights</h3>
        {saving && <span className="ml-auto text-[9px] text-amber-400">Guardando…</span>}
        {!saving && savedTs && (Date.now() - savedTs < 3000) && (
          <span className="ml-auto text-[9px] text-emerald-400">✓ Guardado</span>
        )}
      </div>

      {/* Fan card */}
      <div className="flex-shrink-0 px-3 py-2.5 border-b border-white/5 flex items-center gap-2.5">
        {f.avatar ? (
          <img src={f.avatar} alt="" className="w-9 h-9 rounded-full object-cover" onError={(e) => (e.target.style.display = "none")} />
        ) : (
          <div className="w-9 h-9 rounded-full bg-zinc-800 flex items-center justify-center text-sm">👤</div>
        )}
        <div className="min-w-0 flex-1">
          <div className="text-white text-sm font-semibold truncate">{f.name || selected.fan_name}</div>
          <div className="text-[10px] text-gray-400 truncate">@{f.username || "u" + selected.fan_id}</div>
        </div>
        {f.is_online && <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" title="Online" />}
      </div>

      {/* Body — cambia según tab */}
      <div className="flex-1 min-h-0 overflow-y-auto p-3">
        {loading && !data ? (
          <div className="text-center text-gray-300 text-xs py-6">Cargando…</div>
        ) : tab === "insights" ? (
          <>
            {/* Métricas 2x3 estilo Chatterly */}
            <div className="grid grid-cols-2 gap-2 mb-4">
              <MetricCard label="Total Earned" value={`$${(s.total || 0).toLocaleString()}`} accent />
              <MetricCard label="Sub Duration" value={subDurationDays != null ? `${subDurationDays} days` : "—"} />
              <MetricCard label="Average PPV" value={`$${s.avg_ppv || 0}`} />
              <MetricCard label="Average Tip" value={`$${s.avg_tip || 0}`} />
              <MetricCard
                label={data?.last_purchase?.created_at
                  ? `Last Spent · ${new Date(data.last_purchase.created_at).toLocaleDateString("es-CO", { month: "short", day: "numeric" })}`
                  : "Last Spent"}
                value={data?.last_purchase?.price ? `$${data.last_purchase.price}` : "—"}
              />
              <SpendingPowerCard power={power} />
            </div>

            {/* FAN INFO */}
            <div className="border-t border-white/5 pt-3">
              <h4 className="text-[10px] uppercase tracking-widest text-gray-300 font-semibold mb-2">Fan Info</h4>
              <div className="space-y-2">
                <FieldBlock label="Name (Synced)">
                  <div className="w-full px-2.5 py-1.5 rounded-md bg-zinc-900 border border-white/10 text-white text-xs flex items-center justify-between">
                    <span className="truncate">{f.name || "User"}</span>
                    <span className="text-gray-400 text-[10px]">🔗</span>
                  </div>
                </FieldBlock>
                <FieldBlock label="Source">
                  <input
                    value={sourceDraft}
                    onChange={(e) => setSourceDraft(e.target.value)}
                    placeholder="Source"
                    data-testid="fan-info-source-input"
                    className="w-full px-2.5 py-1.5 rounded-md bg-zinc-900 border border-white/10 text-white text-xs placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                  />
                </FieldBlock>
                <FieldBlock label="Country">
                  <input
                    value={countryDraft}
                    onChange={(e) => setCountryDraft(e.target.value)}
                    placeholder="Select Country"
                    data-testid="fan-info-country-input"
                    className="w-full px-2.5 py-1.5 rounded-md bg-zinc-900 border border-white/10 text-white text-xs placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                  />
                </FieldBlock>
              </div>
            </div>
          </>
        ) : tab === "ppvs" ? (
          <div className="space-y-2">
            <h4 className="text-[10px] uppercase tracking-widest text-fuchsia-400 font-semibold">PPV History</h4>
            {(!data?.ppv_history || data.ppv_history.length === 0) ? (
              <div className="text-center text-gray-400 text-xs py-6">Sin historial de PPV</div>
            ) : (
              data.ppv_history.map((p) => (
                <div key={p.id} className="rounded-lg bg-white/[0.02] border border-white/5 p-2.5 flex items-start gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="text-gray-200 text-xs truncate">{p.text || "PPV message"}</div>
                    <div className="text-gray-400 text-[10px] mt-0.5">
                      {p.created_at ? new Date(p.created_at).toLocaleDateString("es-CO", { day: "numeric", month: "short", year: "2-digit" }) : ""}
                      {" · "}
                      {p.is_opened ? "✓ abierto" : "○ sin abrir"}
                    </div>
                  </div>
                  <span className="text-emerald-400 font-bold text-sm">${p.price}</span>
                </div>
              ))
            )}
          </div>
        ) : (
          // Notes tab
          <div className="space-y-2">
            <h4 className="text-[10px] uppercase tracking-widest text-amber-400 font-semibold">Notas Internas</h4>
            <p className="text-[10px] text-gray-400">Solo el equipo ve estas notas. El fan nunca las recibe.</p>
            <textarea
              value={noteDraft}
              onChange={(e) => setNoteDraft(e.target.value)}
              placeholder="Ej. Prefiere sexting suave, paga rápido los PPVs, tiene esposa..."
              data-testid="fan-note-textarea"
              rows={12}
              className="w-full p-3 rounded-lg bg-zinc-900 border border-white/10 text-white text-xs placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-emerald-500 resize-none"
            />
            <div className="text-[10px] text-gray-400 text-right">
              {noteDraft.length} caracteres · guarda automáticamente
            </div>
          </div>
        )}
      </div>

      {/* Bottom tabs */}
      <div className="flex-shrink-0 border-t border-white/10 flex items-stretch bg-black/40">
        <TabBtn active={tab === "insights"} onClick={() => setTab("insights")} icon="📊" label="Insights" testid="fan-tab-insights" />
        <TabBtn active={tab === "ppvs"} onClick={() => setTab("ppvs")} icon="💎" label="PPVs" testid="fan-tab-ppvs" />
        <TabBtn active={tab === "notes"} onClick={() => setTab("notes")} icon="📝" label="Notes" testid="fan-tab-notes" />
      </div>
    </div>
  );
}

function MetricCard({ label, value, accent }) {
  return (
    <div className="rounded-lg bg-white/[0.03] border border-white/5 px-2.5 py-2">
      <div className="text-[9px] uppercase tracking-wide text-gray-400 truncate">{label}</div>
      <div className={`text-sm font-bold mt-0.5 ${accent ? "text-emerald-400" : "text-white"}`}>{value}</div>
    </div>
  );
}

function SpendingPowerCard({ power }) {
  const dots = 7;
  return (
    <div className="rounded-lg bg-white/[0.03] border border-white/5 px-2.5 py-2">
      <div className="text-[9px] uppercase tracking-wide text-gray-400">Spending Power</div>
      <div className="flex items-center gap-1 mt-1">
        {Array.from({ length: dots }).map((_, i) => (
          <span
            key={i}
            className={`w-2 h-2 rounded-full border ${
              i < power
                ? "bg-fuchsia-500 border-fuchsia-400 shadow-[0_0_6px_rgba(217,70,239,0.5)]"
                : "bg-transparent border-white/20"
            }`}
          />
        ))}
      </div>
    </div>
  );
}

function FieldBlock({ label, children }) {
  return (
    <div>
      <label className="block text-[10px] text-gray-300 mb-1">{label}</label>
      {children}
    </div>
  );
}

function TabBtn({ active, onClick, icon, label, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`flex-1 flex flex-col items-center justify-center py-2 gap-0.5 text-[10px] transition ${
        active
          ? "text-fuchsia-400 border-t-2 border-fuchsia-500 bg-fuchsia-500/5"
          : "text-gray-400 hover:text-white border-t-2 border-transparent"
      }`}
    >
      <span className="text-sm">{icon}</span>
      <span className="font-medium">{label}</span>
    </button>
  );
}

function ToolBtn({ children, onClick, title, testid, active }) {
  return (
    <button
      onClick={onClick}
      title={title}
      data-testid={testid}
      className={`p-1.5 rounded-md transition flex items-center justify-center min-w-[30px] ${
        active
          ? "bg-amber-500/20 border border-amber-500/40 text-amber-300"
          : "hover:bg-white/5 text-gray-300 hover:text-white border border-transparent"
      }`}
    >
      {children}
    </button>
  );
}

